import { useState } from "react";
import { api, ROUTES } from "./api";
import { useAdmin, useAsync, Field, ImageInput, ErrorState, Loading, PageHead } from "./ui";

const PLATFORMS = ["instagram", "facebook", "youtube", "pinterest", "twitter", "linkedin", "whatsapp"];

function LinksEditor({ label, items, onChange }) {
  const update = (i, k, v) => onChange(items.map((it, x) => (x === i ? { ...it, [k]: v } : it)));
  return (
    <div className="span-2">
      <span className="field-label">{label}</span>
      {items.map((it, i) => (
        <div key={i} style={{ display: "flex", gap: 8, margin: "8px 0" }}>
          <input className="input" style={{ flex: 1 }} value={it.label} placeholder="Name" onChange={(e) => update(i, "label", e.target.value)} />
          <input className="input" style={{ flex: 1 }} value={it.to} placeholder="Link (e.g. /shop)" onChange={(e) => update(i, "to", e.target.value)} />
          <button type="button" className="btn sm danger-ghost" onClick={() => onChange(items.filter((_, x) => x !== i))}>Remove</button>
        </div>
      ))}
      <button type="button" className="btn sm" onClick={() => onChange([...items, { label: "", to: "" }])}>Add link</button>
    </div>
  );
}

function SocialEditor({ items, onChange }) {
  const update = (i, k, v) => onChange(items.map((it, x) => (x === i ? { ...it, [k]: v } : it)));
  return (
    <div className="span-2">
      <span className="field-label">Social links</span>
      {items.map((it, i) => (
        <div key={i} style={{ display: "flex", gap: 8, margin: "8px 0" }}>
          <select className="input" style={{ width: 150 }} value={it.platform} onChange={(e) => update(i, "platform", e.target.value)}>
            {PLATFORMS.map((p) => <option key={p} value={p}>{p}</option>)}
          </select>
          <input className="input" style={{ flex: 1 }} value={it.href} placeholder="https://instagram.com/yourpage" onChange={(e) => update(i, "href", e.target.value)} />
          <button type="button" className="btn sm danger-ghost" onClick={() => onChange(items.filter((_, x) => x !== i))}>Remove</button>
        </div>
      ))}
      <button type="button" className="btn sm" onClick={() => onChange([...items, { platform: "instagram", href: "" }])}>Add social link</button>
    </div>
  );
}

export default function FooterInfo() {
  const { data, loading, error, reload } = useAsync(() => api.get(ROUTES.footerinfo));
  if (error) return <ErrorState error={error} onRetry={reload} />;
  if (loading || !data) return <Loading />;
  return <FooterForm initial={data} onSaved={reload} />;
}

function FooterForm({ initial, onSaved }) {
  const { toast } = useAdmin();
  const { logo, ...text } = initial;
  const [f, setF] = useState(text);
  const [logoFile, setLogoFile] = useState(null);
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setF((s) => ({ ...s, [k]: e.target.value }));
  const setList = (k) => (v) => setF((s) => ({ ...s, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const cleanLinks = (l) => l.map((x) => ({ label: x.label.trim(), to: x.to.trim() })).filter((x) => x.label && x.to);
      const payload = {
        ...f,
        quickLinks: cleanLinks(f.quickLinks),
        careLinks: cleanLinks(f.careLinks),
        social: f.social.map((s) => ({ ...s, href: s.href.trim() })).filter((s) => s.href),
      };
      const form = new FormData();
      form.append("data", JSON.stringify(payload));
      if (logoFile) form.append("logo", logoFile);
      await api.upload("PUT", ROUTES.footerinfo, form);
      toast("Footer saved");
      setLogoFile(null);
      onSaved();
    } catch (err) {
      toast(err.message, "err");
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <PageHead
        title="Footer"
        sub="Edit everything shown at the bottom of the site"
        actions={<button className="btn primary" form="footer-form" disabled={saving}>{saving ? "Saving…" : "Save changes"}</button>}
      />
      <form id="footer-form" onSubmit={submit} className="form-grid">
        <div className="span-2">
          <span className="field-label">Logo</span>
          <ImageInput current={logo} file={logoFile} onChange={setLogoFile} />
        </div>

        <Field label="Quick links title"><input className="input" value={f.quickLinksTitle} onChange={set("quickLinksTitle")} /></Field>
        <div />
        <LinksEditor label="Quick links" items={f.quickLinks} onChange={setList("quickLinks")} />

        <Field label="Customer care title"><input className="input" value={f.careTitle} onChange={set("careTitle")} /></Field>
        <div />
        <LinksEditor label="Customer care links" items={f.careLinks} onChange={setList("careLinks")} />

        <Field label="Follow us title"><input className="input" value={f.followTitle} onChange={set("followTitle")} /></Field>
        <div />
        <SocialEditor items={f.social} onChange={setList("social")} />

        <Field label="Newsletter title"><input className="input" value={f.newsletterTitle} onChange={set("newsletterTitle")} /></Field>
        <Field label="Tagline (bottom right)"><input className="input" value={f.tagline} onChange={set("tagline")} /></Field>
        <div className="span-2">
          <Field label="Copyright text" hint="The year is added automatically before this text">
            <input className="input" value={f.copyright} onChange={set("copyright")} />
          </Field>
        </div>
      </form>
    </>
  );
}